'use client'

import React from 'react'

type Props = {
    func: 'sin' | 'cos' | 'tan'
    angleDegrees: number
}

const WIDTH = 340
const HEIGHT = 200
const PAD_LEFT = 30
const PAD_RIGHT = 10
const PAD_TOP = 10
const PAD_BOTTOM = 24
const PLOT_W = WIDTH - PAD_LEFT - PAD_RIGHT
const PLOT_H = HEIGHT - PAD_TOP - PAD_BOTTOM

const X_MIN = -360
const X_MAX = 360
const Y_MIN_LINEAR = -1.3
const Y_MAX_LINEAR = 1.3
const Y_MIN_TAN = -5
const Y_MAX_TAN = 5

function evalFunc(func: 'sin' | 'cos' | 'tan', deg: number): number {
    const rad = (deg * Math.PI) / 180
    if (func === 'sin') return Math.sin(rad)
    if (func === 'cos') return Math.cos(rad)
    return Math.tan(rad)
}

export default function TrigWaveChart({ func, angleDegrees }: Props) {
    const yMin = func === 'tan' ? Y_MIN_TAN : Y_MIN_LINEAR
    const yMax = func === 'tan' ? Y_MAX_TAN : Y_MAX_LINEAR

    const xToSvg = (deg: number) => PAD_LEFT + ((deg - X_MIN) / (X_MAX - X_MIN)) * PLOT_W
    const yToSvg = (val: number) => PAD_TOP + ((yMax - val) / (yMax - yMin)) * PLOT_H

    const segments: Array<Array<[number, number]>> = []
    let current: Array<[number, number]> = []
    const step = 1
    for (let deg = X_MIN; deg <= X_MAX; deg += step) {
        const val = evalFunc(func, deg)
        if (func === 'tan' && Math.abs(val) > Y_MAX_TAN) {
            if (current.length > 1) segments.push(current)
            current = []
            continue
        }
        current.push([xToSvg(deg), yToSvg(val)])
    }
    if (current.length > 1) segments.push(current)

    const pathD = (points: Array<[number, number]>) =>
        points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')

    const markerAngle = ((angleDegrees % 360) + 540) % 360 - 180
    const markerValue = evalFunc(func, angleDegrees)
    const markerClipped = func === 'tan' && Math.abs(markerValue) > Y_MAX_TAN
    const markerX = xToSvg(markerAngle)
    const markerY = yToSvg(Math.max(yMin, Math.min(yMax, markerValue)))

    const gridDegrees = [-360, -270, -180, -90, 0, 90, 180, 270, 360]
    const color = func === 'sin' ? '#2563eb' : func === 'cos' ? '#16a34a' : '#dc2626'

    return (
        <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{ maxWidth: '100%', height: 'auto' }}>
            {/* zero line */}
            <line x1={PAD_LEFT} y1={yToSvg(0)} x2={WIDTH - PAD_RIGHT} y2={yToSvg(0)} stroke="#d1d5db" strokeWidth={1} />
            {/* vertical gridlines */}
            {gridDegrees.map((deg) => (
                <g key={deg}>
                    <line x1={xToSvg(deg)} y1={PAD_TOP} x2={xToSvg(deg)} y2={HEIGHT - PAD_BOTTOM} stroke="#f3f4f6" strokeWidth={1} />
                    <text x={xToSvg(deg)} y={HEIGHT - 6} fontSize={9} textAnchor="middle" fill="#9ca3af">
                        {deg}°
                    </text>
                </g>
            ))}
            {/* y axis labels */}
            {func === 'tan'
                ? [-5, 0, 5].map((v) => (
                      <text key={v} x={4} y={yToSvg(v) + 3} fontSize={9} fill="#9ca3af">
                          {v}
                      </text>
                  ))
                : [-1, 0, 1].map((v) => (
                      <text key={v} x={4} y={yToSvg(v) + 3} fontSize={9} fill="#9ca3af">
                          {v}
                      </text>
                  ))}
            {/* curve segments */}
            {segments.map((seg, i) => (
                <path key={i} d={pathD(seg)} fill="none" stroke={color} strokeWidth={2} />
            ))}
            {/* current angle marker */}
            {!markerClipped && (
                <>
                    <line x1={markerX} y1={PAD_TOP} x2={markerX} y2={HEIGHT - PAD_BOTTOM} stroke={color} strokeWidth={1} strokeDasharray="3,3" opacity={0.5} />
                    <circle cx={markerX} cy={markerY} r={4} fill={color} stroke="#fff" strokeWidth={1.5} />
                </>
            )}
        </svg>
    )
}
